import React, {useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";
import {Checkbox} from "primereact/checkbox";
import {useGetDatasetsInfo} from "api/api-hooks/datasets-hooks";
import CopyTextWithButton from "../copy-text-with-button/CopyTextWithButton";
import {DatasetInfo} from "../../../api/model/dataset/DatasetInfo";

type DatasetsPropertiesTableProps = {
    identifiers: string[]
}

type ShowColumnType = {
    id: boolean,
    group: boolean,
    dataType: boolean,
    unit: boolean,
    category: boolean,
    datasetClass: boolean,
    accessType: boolean,
    decimalPlaces: boolean,
    tags: boolean
}

const ShowColumnTypeTitles: Record<keyof ShowColumnType, string> = {
    id: "ID",
    group: "Group",
    dataType: "Data type",
    unit: "Unit",
    category: "Category",
    datasetClass: "Dataset class",
    accessType: "Access type",
    decimalPlaces: "Decimal places",
    tags: "Tags"
}

const DatasetsPropertiesTable = ({identifiers}: DatasetsPropertiesTableProps) => {
    const {data, isLoading, error} = useGetDatasetsInfo(identifiers)

    const [showColumn, setShowColumn] = useState<ShowColumnType>({
        id: true,
        group: true,
        dataType: true,
        unit: false,
        category: false,
        datasetClass: false,
        accessType: true,
        decimalPlaces: false,
        tags: false
    })

    const groupBody = (rowData: DatasetInfo) => {
        if (rowData.groupTitle && rowData.groupId) {
            return `${rowData.groupTitle} [${rowData.groupId}]`;
        } else if (rowData.groupTitle) {
            return rowData.groupTitle;
        } else if (rowData.groupId) {
            return `[${rowData.groupId}]`;
        } else {
            return "";
        }
    };

    const categoryBody = (rowData: DatasetInfo) => {
        if (rowData.categoryTitle && rowData.categoryId) {
            return `${rowData.categoryTitle} [${rowData.categoryId}]`;
        } else if (rowData.categoryTitle) {
            return rowData.categoryTitle;
        } else if (rowData.categoryId) {
            return `[${rowData.categoryId}]`;
        } else {
            return "";
        }
    }

    const datasetClassBody = (rowData: DatasetInfo) => {
        return <Tag value={rowData.datasetClass}></Tag>
    }

    const tagsBody = (rowData: DatasetInfo) => {
        return <div className={"flex gap-1 flex-wrap"}>
            {rowData.tags.map((e, index) => <Tag value={e} key={index}></Tag>)}
        </div>
    }

    if (error) {
        return <div>Failed to fetch dataset information: {error.message}</div>;
    }

    return <div className={"flex flex-column gap-1"}>
        <div className={"flex gap-1 align-items-center"}>
            <b>SHOW:</b>
            <div className={"flex gap-3 align-items-center"}>
                {Object.entries(showColumn).map(([key, value]) => {
                    return <div className={"flex gap-1 align-items-center"} key={key}>
                        <span>{ShowColumnTypeTitles[key as keyof ShowColumnType]}</span>
                        <Checkbox checked={value}
                                  onChange={e => setShowColumn({...showColumn, [key]: e.checked})}
                        />

                    </div>

                })}
            </div>
        </div>
        <DataTable value={data?.map(e => ({...e, id: e.identifier?.split(":")[0]}))} loading={isLoading} size={"small"}>
            <Column header={"Title"} field={"title"}></Column>
            {showColumn.id && <Column header={"ID"}
                                      field={"id"}
                                      body={e => <CopyTextWithButton text={e.id}
                                                                     copyableText={e.identifier}/>}>
            </Column>}
            {showColumn.unit && <Column header={"Unit"} field={"unit"}></Column>}
            {showColumn.group && <Column header={"Group"} field={"groupId"} body={groupBody}></Column>}
            {showColumn.category && <Column header={"Category"} field={"categoryId"} body={categoryBody}></Column>}
            {showColumn.dataType && <Column header={"Data Type"} field={"dataType"}></Column>}
            {showColumn.datasetClass &&
                <Column header={"Dataset Class"} field={"datasetClass"} body={datasetClassBody}></Column>}
            {showColumn.accessType && <Column header={"Access Type"} field={"accessType"}></Column>}
            {showColumn.decimalPlaces && <Column header={"Decimal Places"} field={"decimalPlaces"}></Column>}
            {showColumn.tags &&
                <Column header={"Tags"} field={"tags"} style={{width: "100px"}} body={tagsBody}></Column>}
        </DataTable>
    </div>
}

export default DatasetsPropertiesTable;